import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarBlank, VideoCamera, Spinner, XCircle, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { API } from "@/lib/site";

export default function ManageBooking() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState("view"); // view | reschedule
  const [avail, setAvail] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [attending, setAttending] = useState(false);

  const confirmAttendance = useCallback(async (silent) => {
    try {
      const res = await fetch(`${API}/booking/manage/${token}/confirm`, { method: "POST" });
      if (!res.ok) throw new Error();
      setAttending(true);
      setBooking((b) => (b ? { ...b, attendance_confirmed: true } : b));
      if (!silent) toast.success("Thanks — your attendance is confirmed!");
    } catch {
      if (!silent) toast.error("Could not confirm. Please try again.");
    }
  }, [token]);

  const load = useCallback(async () => {
    if (!token) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API}/booking/manage/${token}`);
      if (!res.ok) throw new Error();
      const d = await res.json();
      setBooking(d.booking);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (booking && params.get("confirm") === "1" && !booking.attendance_confirmed) {
      confirmAttendance(true);
    }
  }, [booking, params, confirmAttendance]);

  const startReschedule = async () => {
    setMode("reschedule");
    try {
      const res = await fetch(`${API}/booking/availability?session_type=${booking.session_type}`);
      const d = await res.json();
      setAvail(d);
      if (d.availability?.length) setSelectedDate(d.availability[0].date);
    } catch {
      toast.error("Could not load availability.");
    }
  };

  const submitReschedule = async () => {
    if (!selectedSlot) return toast.error("Pick a new time.");
    setBusy(true);
    try {
      const res = await fetch(`${API}/booking/manage/${token}/reschedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: selectedDate, start_time: selectedSlot.start }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not reschedule");
      setBooking(d.booking);
      setMode("view");
      setSelectedSlot(null);
      toast.success("Your session has been moved.");
    } catch (err) {
      toast.error(err.message || "Could not reschedule");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/booking/manage/${token}/cancel`, { method: "POST" });
      if (!res.ok) throw new Error();
      setBooking({ ...booking, status: "cancelled" });
      toast.success("Your session has been cancelled.");
    } catch {
      toast.error("Could not cancel. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-brand-bg flex items-center justify-center">
        <Spinner size={40} className="animate-spin text-brand-gold" />
      </main>
    );
  }

  if (notFound) {
    return (
      <main className="min-h-screen bg-brand-bg flex items-center justify-center px-6">
        <div className="text-center">
          <XCircle size={54} className="text-brand-gold mx-auto" />
          <h1 className="font-serif text-3xl text-brand-text mt-6">Booking not found</h1>
          <p className="text-brand-dim mt-2">This link may be invalid or expired.</p>
          <Link to="/" className="text-brand-gold text-sm mt-6 inline-block">← Back to home</Link>
        </div>
      </main>
    );
  }

  const activeDay = avail?.availability?.find((d) => d.date === selectedDate);
  const cancelled = booking.status === "cancelled";

  return (
    <main className="min-h-screen bg-brand-bg flex items-center justify-center px-6 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        data-testid="manage-booking-page"
        className="w-full max-w-xl rounded-3xl border border-brand-line bg-brand-surface p-8 lg:p-10"
      >
        <span className="overline">Manage your booking</span>
        <h1 className="font-serif text-3xl lg:text-4xl text-brand-text mt-3">
          {booking.session_name}
        </h1>

        {cancelled ? (
          <div className="mt-6 flex items-center gap-2 text-brand-dim" data-testid="booking-cancelled-state">
            <XCircle size={20} className="text-brand-gold" /> This session has been cancelled.
          </div>
        ) : (
          <div className="mt-6 space-y-3 text-brand-dim">
            <p className="flex items-center gap-2">
              <CalendarBlank size={18} className="text-brand-gold" />
              <span className="text-brand-text" data-testid="booking-when">
                {booking.date} · {booking.start_time} {booking.tz_label}
              </span>
            </p>
            {booking.meeting_link && (
              <p className="flex items-center gap-2">
                <VideoCamera size={18} className="text-brand-gold" />
                <a href={booking.meeting_link} target="_blank" rel="noreferrer" className="text-brand-gold break-all">
                  {booking.meeting_link}
                </a>
              </p>
            )}
          </div>
        )}

        {!cancelled && (attending || booking.attendance_confirmed) && (
          <div className="mt-6 flex items-center gap-2 text-brand-sage" data-testid="attendance-confirmed">
            <CheckCircle size={20} weight="fill" /> Attendance confirmed — see you there!
          </div>
        )}

        {!cancelled && mode === "view" && (
          <div className="mt-8 flex flex-wrap gap-3">
            {!(attending || booking.attendance_confirmed) && (
              <button
                data-testid="confirm-attendance"
                onClick={() => confirmAttendance(false)}
                className="rounded-full bg-brand-gold px-6 py-3 text-sm font-medium text-brand-bg hover:bg-brand-text transition-colors"
              >
                I'll be there
              </button>
            )}
            <button
              data-testid="reschedule-start"
              onClick={startReschedule}
              className="rounded-full bg-brand-text px-6 py-3 text-sm font-medium text-brand-bg hover:bg-brand-gold transition-colors"
            >
              Reschedule
            </button>
            {booking.gcal_link && (
              <a
                data-testid="gcal-link"
                href={booking.gcal_link}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-brand-line px-6 py-3 text-sm text-brand-text hover:border-brand-gold hover:text-brand-gold transition-colors"
              >
                Add to Google Calendar
              </a>
            )}
            <a
              data-testid="ics-link"
              href={`${API}/booking/manage/${token}/ics`}
              className="rounded-full border border-brand-line px-6 py-3 text-sm text-brand-text hover:border-brand-gold hover:text-brand-gold transition-colors"
            >
              Apple/Outlook (.ics)
            </a>
            {!confirmingCancel ? (
              <button
                data-testid="cancel-booking"
                onClick={() => setConfirmingCancel(true)}
                className="rounded-full border border-brand-line px-6 py-3 text-sm text-brand-text hover:border-brand-gold hover:text-brand-gold transition-colors"
              >
                Cancel session
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  data-testid="cancel-confirm"
                  onClick={cancel}
                  disabled={busy}
                  className="rounded-full bg-brand-gold px-6 py-3 text-sm font-medium text-brand-bg hover:bg-brand-text transition-colors disabled:opacity-60"
                >
                  {busy ? "Cancelling…" : "Yes, cancel"}
                </button>
                <button
                  data-testid="cancel-keep"
                  onClick={() => setConfirmingCancel(false)}
                  className="text-sm text-brand-dim hover:text-brand-text transition-colors"
                >
                  Keep it
                </button>
              </div>
            )}
          </div>
        )}

        {!cancelled && mode === "reschedule" && (
          <div className="mt-8">
            <button
              onClick={() => setMode("view")}
              className="text-sm text-brand-dim hover:text-brand-text flex items-center gap-1 mb-5"
            >
              <ArrowLeft size={14} /> Back
            </button>
            {!avail ? (
              <div className="flex items-center gap-2 text-brand-dim py-8 justify-center">
                <Spinner size={20} className="animate-spin" /> Loading times…
              </div>
            ) : (
              <>
                <div className="flex gap-3 overflow-x-auto pb-3">
                  {avail.availability.map((d) => (
                    <button
                      key={d.date}
                      data-testid={`reschedule-date-${d.date}`}
                      onClick={() => {
                        setSelectedDate(d.date);
                        setSelectedSlot(null);
                      }}
                      className={`flex-shrink-0 w-20 rounded-2xl border px-3 py-4 text-center transition-colors ${
                        selectedDate === d.date ? "border-brand-gold bg-brand-gold/10" : "border-brand-line"
                      }`}
                    >
                      <span className="overline block text-brand-dim">{d.weekday}</span>
                      <span className="block font-serif text-lg text-brand-text mt-1">{d.day.split(" ")[1]}</span>
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-5">
                  {activeDay?.slots.map((s) => (
                    <button
                      key={s.start}
                      data-testid={`reschedule-slot-${s.start}`}
                      onClick={() => setSelectedSlot(s)}
                      className={`rounded-xl border py-3 text-sm transition-colors ${
                        selectedSlot?.start === s.start
                          ? "border-brand-gold bg-brand-gold text-brand-bg font-medium"
                          : "border-brand-line text-brand-text hover:border-brand-gold"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="reschedule-submit"
                  onClick={submitReschedule}
                  disabled={busy || !selectedSlot}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-text px-6 py-4 text-sm font-medium text-brand-bg hover:bg-brand-gold transition-colors disabled:opacity-50"
                >
                  {busy ? <Spinner size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                  Confirm new time
                </button>
              </>
            )}
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-brand-line">
          <Link to="/" data-testid="manage-back-home" className="text-sm text-brand-gold hover:text-brand-text transition-colors">
            ← Back to TonyBrand29
          </Link>
        </div>
      </motion.div>
    </main>
  );
}
